// src/pages/providers/2_dashboard/components/ProviderAccountSetupForm.js
import React, { useState, useEffect, useContext, useMemo } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import Select from 'react-select';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import { ChevronLeftIcon, ChevronRightIcon, CheckIcon } from '@heroicons/react/solid';
import { functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { getConfigProviderAccountSetup } from '../config/providerAccountSetupConfig';
import ButtonExitModal from '../../../../components/ButtonExitModal';
import { isProjectAllowedToUseMicrosoftSSO } from '../../../../utils/auth/microsoftProvider';
import { sanitizeNameInput, sanitizeEmailInput } from '../../../../utils/inputSanitization';
import { normaliseProviderRole } from '../../../../utils/providerRole';
import { PATHWAY_SPECIALISM_LABELS, getSelectablePathwaySpecialisms } from '../../../../utils/pathwaySpecialismUtils';
import getScrollablePortalTarget from '../../../../utils/getScrollablePortalTarget';

/**
 * Shared react-select props for dropdowns inside this Radix AlertDialog.
 * Portals the menu to a scroll-safe container on <body> so it escapes the
 * dialog's overflow clipping while remaining scrollable despite Radix's
 * react-remove-scroll lock (see getScrollablePortalTarget for details).
 */
const PROVIDER_SETUP_SELECT_MENU_PROPS = {
    classNamePrefix: 'td-select',
    menuPortalTarget: getScrollablePortalTarget(),
    menuPosition: 'fixed',
    styles: {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    },
};

/**
 * Provider "User Type" modelling (important context for future maintainers)
 * -----------------------------------------------------------------------
 * Business requirement:
 * - Some organisations need Org. Admins who are NOT clinical staff.
 * - These non-clinical Org. Admins must keep Org. Admin management permissions,
 *   but must NOT appear in clinician assignment dropdowns and must NOT be assignable
 *   to clinical assessment stages.
 *
 * Design decision:
 * - We keep backend `userType` values unchanged (Clinician | Support | Org. Admin) because
 *   those values are deeply embedded in Firestore Rules and backend permission checks.
 * - We expose two Org. Admin variants in the UI only:
 *     - "Clinical Org. Admin"      => userType: 'Org. Admin', isAssignableClinician: true
 *     - "Non-Clinical Org. Admin"  => userType: 'Org. Admin', isAssignableClinician: false
 * - The new boolean flag is stored separately and can be used for filtering / defence-in-depth
 *   without changing the core authorization model.
 */
const UI_USER_TYPES = Object.freeze({
    CLINICIAN: 'Clinician',
    SUPPORT: 'Support',
    CLINICAL_ORG_ADMIN: 'Clinical Org. Admin',
    NON_CLINICAL_ORG_ADMIN: 'Non-Clinical Org. Admin',
});

function getUiUserTypeValue(selection) {
    // Our "selectOneDropdown" is implemented with react-select `isMulti`,
    // so we may receive:
    // - a string (already processed),
    // - an object { value, label },
    // - an array [{ value, label }]
    return (
        (typeof selection === 'object' && selection?.value) ||
        (Array.isArray(selection) && selection[0]?.value) ||
        selection
    );
}

function shouldSkipProfessionalRegistrationStep(uiUserTypeValue) {
    // Business rule:
    // - Support users and Non-Clinical Org Admins are not clinical staff.
    // - Therefore they do not need (and should not be prompted for) professional registration details.
    return (
        uiUserTypeValue === UI_USER_TYPES.SUPPORT ||
        uiUserTypeValue === UI_USER_TYPES.NON_CLINICAL_ORG_ADMIN
    );
}

function mapUiUserTypeToBackend(uiUserType) {
    switch (uiUserType) {
        case UI_USER_TYPES.CLINICIAN:
            return { userType: 'Clinician', isAssignableClinician: true };
        case UI_USER_TYPES.SUPPORT:
            return { userType: 'Support', isAssignableClinician: false };
        case UI_USER_TYPES.CLINICAL_ORG_ADMIN:
            return { userType: 'Org. Admin', isAssignableClinician: true };
        case UI_USER_TYPES.NON_CLINICAL_ORG_ADMIN:
            return { userType: 'Org. Admin', isAssignableClinician: false };
        default:
            // Fail safe: if we ever get an unknown value, treat it as invalid rather than guessing.
            return { userType: null, isAssignableClinician: null };
    }
}


function ProviderAccountSetupForm({ isOpen, onClose }) {
    const { userData, refreshUserData } = useContext(GlobalStateContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState('');
    const [currentStep, setCurrentStep] = useState(1);
    // Determine sign-in policy from environment + organisation config
    const canSelectSSO = isProjectAllowedToUseMicrosoftSSO();
    const isNonProdEnv = (import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.VITE_ENVIRONMENT === 'staging');

    // Get the organization-specific configurationf
    const orgConfig = useMemo(() => {
        return getConfigProviderAccountSetup(userData?.organisation);
    }, [userData?.organisation]);

    // ------------------------------------------------------------------
    // Dev/Staging-only authMode override (SLaM + 3 Borough)
    // ------------------------------------------------------------------
    //
    // Business goal:
    // - In production, SLaM/3 Borough must always use Microsoft-first invite onboarding.
    // - In dev/staging, we sometimes need to test the legacy email/password (hybrid) flow
    //   for these orgs without changing production policy.
    //
    // Security model:
    // - This is UI-only convenience. The backend MUST enforce production behavior regardless
    //   of what the client sends (we will implement that in `setupNewClinician`).
    const isAuthModeOverrideOrg = useMemo(() => {
        return userData?.organisation === 'SLaM' || userData?.organisation === '3 Borough Adult ADHD and Autism Service';
    }, [userData?.organisation]);

    const canOverrideAuthModeInUi = Boolean(
        isNonProdEnv &&
        isAuthModeOverrideOrg &&
        orgConfig?.allowAuthModeOverrideInNonProd === true
    );

    // Only offer Microsoft-first mode if the current project/build has Microsoft SSO enabled.
    // Otherwise selecting it would strand the invited user (invite created, but no SSO button).
    const authModeOptionsForUi = useMemo(() => {
        const raw = Array.isArray(orgConfig?.authModeOptions) ? orgConfig.authModeOptions : [];
        const normalized = raw.map(v => String(v || '').toLowerCase()).filter(Boolean);
        const filtered = normalized.filter(v => v !== 'with_microsoft_sso' || canSelectSSO);
        // De-dupe while preserving order.
        return Array.from(new Set(filtered));
    }, [orgConfig?.authModeOptions, canSelectSSO]);

    const shouldShowAuthModeSelector = canOverrideAuthModeInUi && authModeOptionsForUi.length > 1;
    const [selectedAuthMode, setSelectedAuthMode] = useState('hybrid');

    // ──────────────────────────────────────────────────────────────────────────
    // Pathway Specialism state
    // ──────────────────────────────────────────────────────────────────────────
    //
    // Each clinician can specialise in one or more assessment pathways (e.g.
    // Adult ADHD, Adult Autism). The selector is only relevant when the
    // organisation supports multiple pathways; single-pathway orgs hide it.
    //
    // Default: ['adultAdhd'] — matches the backwards-compatibility default in
    // pathwaySpecialismUtils.getEffectiveSpecialisms so new clinicians created
    // before this feature existed are treated identically.
    const [pathwaySpecialisms, setPathwaySpecialisms] = useState(['adultAdhd']);

    // Tracks which assessment modules the organisation has enabled.
    // null = not yet fetched (we default to showing the selector for safety).
    // Fetched via getOrgConfig, matching the pattern used by PatientSetupForm.
    const [availableOrgModules, setAvailableOrgModules] = useState(null);

    // Get total steps from config
    const totalSteps = useMemo(() => {
        return Object.keys(orgConfig.steps).length;
    }, [orgConfig]);

    // Initialize form data structure
    const [formData, setFormData] = useState({});

    // This wizard includes a "Professional Registration" step that is only relevant for clinical staff.
    // For Support and Non-Clinical Org Admins, we hide/skip that step cleanly (2-step flow).
    const selectedUiUserTypeValue = useMemo(() => getUiUserTypeValue(formData?.userType), [formData?.userType]);
    const shouldSkipProfessionalRegistration = useMemo(
        () => shouldSkipProfessionalRegistrationStep(selectedUiUserTypeValue),
        [selectedUiUserTypeValue]
    );
    const effectiveTotalSteps = useMemo(() => {
        // Default config uses 3 steps and step "3" is Professional Registration.
        // We intentionally do NOT mutate orgConfig; we only change the navigation/display.
        if (shouldSkipProfessionalRegistration && orgConfig?.steps?.['3']) {
            return Math.max(1, totalSteps - 1);
        }
        return totalSteps;
    }, [shouldSkipProfessionalRegistration, orgConfig, totalSteps]);

    // Initialize form data structure - update to use isCustomField from config
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(1);
            setSubmitError('');
            setSubmitSuccess('');

            // Initialize form data
            const initialData = {
                customFields: {}
            };

            // Check if orgConfig and steps exist before processing
            if (orgConfig && orgConfig.steps) {
                // Initialize fields from all steps
                Object.keys(orgConfig.steps).forEach(stepKey => {
                    const step = orgConfig.steps[stepKey];

                    // Check if step and fields exist
                    if (step && step.fields) {
                        Object.keys(step.fields).forEach(fieldKey => {
                            const field = step.fields[fieldKey];
                            if (field && field.varName) {
                                // Initialize regular fields
                                initialData[field.varName] = '';

                                // For custom fields, initialize in the customFields object too
                                if (field.isCustomField) {
                                    initialData.customFields[field.varName] = '';
                                }
                            }
                        });
                    }
                });
            }

            setFormData(initialData);

            // Reset pathway specialisms to default whenever the modal opens.
            setPathwaySpecialisms(['adultAdhd']);
            setAvailableOrgModules(null);

            // Reset auth mode selector whenever the modal opens so the default is predictable.
            // Default selection comes from the organisation config (currently with_microsoft_sso for SLaM/3 Borough),
            // but we fall back to the first allowed option to avoid invalid state.
            const defaultAuthMode = String(orgConfig?.authMode || 'hybrid').toLowerCase();
            const safeDefault = authModeOptionsForUi.includes(defaultAuthMode)
                ? defaultAuthMode
                : (authModeOptionsForUi[0] || 'hybrid');
            setSelectedAuthMode(safeDefault);
        }
    }, [isOpen, orgConfig, authModeOptionsForUi]);

    // Fetch the org's available assessment modules on open. This determines
    // whether we show the pathway specialism selector. Single-module orgs
    // (the majority today) do not need it. The result is NOT cached across
    // modal opens because org config could change between sessions.
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        const fetchModules = async () => {
            try {
                const getOrgConfigFn = httpsCallable(functions, 'getOrgConfig');
                const result = await getOrgConfigFn();
                if (cancelled) return;
                const modules = result?.data?.availableAssessmentModules;
                if (Array.isArray(modules) && modules.length > 0) {
                    setAvailableOrgModules(modules);

                    // Auto-set pathway specialisms based on the org's available modules:
                    // - Single-module orgs: pre-select the one module (selector stays hidden).
                    // - Multi-module orgs: pre-select all modules so the admin can deselect
                    //   any they don't want rather than starting from a partial default.
                    setPathwaySpecialisms(modules);
                }
            } catch (_) {
                // Non-fatal: if we cannot determine the org's modules, we leave
                // availableOrgModules as null so the selector is shown for safety.
            }
        };
        fetchModules();
        return () => { cancelled = true; };
    }, [isOpen]);

    // Determine whether to show the pathway selector.
    //
    // Visibility rules:
    // 1. User type must be an assignable clinical role (Clinician or Clinical
    //    Org. Admin). Support and Non-Clinical Org. Admins cannot be assigned
    //    to patients, so pathway selection is irrelevant for them.
    // 2. The org must have multiple assessment modules, OR we could not determine the
    //    org's modules (safety default: show the selector so pathways are not silently
    //    defaulted without the admin's knowledge).
    const showPathwaySpecialisms = useMemo(() => {
        if (shouldSkipProfessionalRegistrationStep(selectedUiUserTypeValue)) return false;
        if (availableOrgModules === null) return true;
        return availableOrgModules.length > 1;
    }, [selectedUiUserTypeValue, availableOrgModules]);

    // Only offer pathways this org actually runs. Well Life Clinic therefore
    // sees CYP ADHD; The Owl Centre does not. When org modules have not loaded
    // we still list the full known set (same safety default as the selector
    // visibility rule above).
    const selectablePathwaySpecialisms = useMemo(
        () => getSelectablePathwaySpecialisms(availableOrgModules, pathwaySpecialisms),
        [availableOrgModules, pathwaySpecialisms],
    );

    const validateEmail = (email) => {
        if (!email) return true;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Handle input changes for any field
    const handleInputChange = (fieldName, value, isMultiButSingle = false) => {

        // Find field config to check if it's a custom field
        const isCustomField = (() => {
            // Check all steps for this field
            for (const stepKey in orgConfig.steps) {
                const step = orgConfig.steps[stepKey];
                for (const fieldKey in step.fields) {
                    const field = step.fields[fieldKey];
                    if (field.varName === fieldName && field.isCustomField === true) {
                        return true;
                    }
                }
            }
            return false;
        })();

        // For custom fields, store in the customFields object
        if (isCustomField) {
            setFormData(prev => {
                // Handle dropdown selections (both single and multi)
                let processedValue = value;

                // For React Select single selection (using isMulti styling)
                if (Array.isArray(value) && value.length > 0) {
                    processedValue = value[0].value;
                }
                // For regular React Select single selection
                else if (value && typeof value === 'object' && 'value' in value) {
                    processedValue = value.value;
                }

                return {
                    ...prev,
                    customFields: {
                        ...prev.customFields || {},
                        [fieldName]: processedValue
                    },
                    // Also keep in main object for UI
                    [fieldName]: value
                };
            });
        } else {

            // Handle regular fields
            setFormData(prev => ({
                ...prev,
                [fieldName]: value
            }));

            // Special case for email validation
            if (fieldName === 'email') {
                const isValid = validateEmail(value);
                if (!isValid && value) {
                    setSubmitError('Please enter a valid email address');
                } else {
                    setSubmitError('');
                }
            }
        }
    };

    // Validate current step — checks the step config's required fields and,
    // on the final step, also ensures a pathway specialism is selected when the
    // pathway selector is visible (clinical users in multi-pathway orgs).
    const isStepValid = () => {
        const currentStepConfig = orgConfig.steps[currentStep.toString()];
        if (!currentStepConfig) return false;

        // For empty fields object (placeholder steps), return true
        if (Object.keys(currentStepConfig.fields).length === 0) return true;

        // Check that required fields are filled
        const fieldsValid = Object.values(currentStepConfig.fields).every(field => {
            // Skip validation for non-required fields
            if (field.required !== true) return true;

            const value = formData[field.varName];

            // Role is the job title on letters. Spaces-only, a second line,
            // or a title past the length limit do not count as filled in.
            // Registration fields are not checked here; they stay optional.
            if (field.varName === 'role') {
                return normaliseProviderRole(typeof value === 'string' ? value : '').ok;
            }

            // Email also needs to be valid if provided
            if (field.varName === 'email' && value) {
                return validateEmail(value);
            }

            // For multi-select fields, check that at least one option is selected
            if (field.inputType === 'selectMultipleDropdown') {
                return Array.isArray(value) && value.length > 0;
            }

            // For single-select fields using isMulti styling
            if (field.inputType === 'selectOneDropdown') {
                return Array.isArray(value) ? value.length > 0 : Boolean(value);
            }

            // All other fields just need to be non-empty
            return value !== undefined && value !== '';
        });

        if (!fieldsValid) return false;

        // On the final step, clinical users must select at least one pathway
        // when the pathway selector is visible.
        if (currentStep === effectiveTotalSteps && showPathwaySpecialisms) {
            if (!Array.isArray(pathwaySpecialisms) || pathwaySpecialisms.length === 0) {
                return false;
            }
        }

        return true;
    };

    // Add this function to determine if the current step is the final step
    const isFinalStep = useMemo(() => {
        // If we're on the last *effective* step, it's final.
        // (For Support and Non-Clinical Org Admins, the professional registration step is skipped.)
        return currentStep === effectiveTotalSteps;
    }, [currentStep, effectiveTotalSteps]);

    // Handle Next step click.
    // Note: For Support and Non-Clinical Org Admins, `effectiveTotalSteps` is reduced so we submit on step 2.
    const handleNext = async () => {
        // If we're on the first step and there's an email, validate it
        if (currentStep === 1 && formData.email) {
            // Check if the email is valid
            if (!validateEmail(formData.email)) {
                setSubmitError('Please enter a valid email address');
                return;
            }
        }

        // If all validation passes, proceed to next step
        if (currentStep < effectiveTotalSteps) {
            setCurrentStep(prev => prev + 1);
        } else {
            // This is the final step - submit the form
            await handleSubmit();
        }
    };

    // Handle back button click
    const handleBack = () => {
        // For Support and Non-Clinical Org Admins, the wizard never navigates to the
        // professional registration step, so a standard decrement is sufficient.
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleSubmit = async (e) => {
        setIsSubmitting(true);
        setSubmitError('');
        setSubmitSuccess('');
        console.log("formData", formData);

        try {
            // Validate email if provided
            if (formData.email && !validateEmail(formData.email)) {
                setSubmitError('Please enter a valid email address');
                setIsSubmitting(false);
                return;
            }

            // Process form data to extract values from select fields
            const processedData = {};
            const customFields = {};

            // Process all form fields
            Object.keys(formData).forEach(key => {
                let value = formData[key];

                // Skip customFields object itself
                if (key === 'customFields') return;

                // Find if this is a custom field
                const isCustomField = (() => {
                    for (const stepKey in orgConfig.steps) {
                        const step = orgConfig.steps[stepKey];
                        for (const fieldKey in step.fields) {
                            const field = step.fields[fieldKey];
                            if (field.varName === key && field.isCustomField === true) {
                                return true;
                            }
                        }
                    }
                    return false;
                })();

                // Find field input type
                const inputType = (() => {
                    for (const stepKey in orgConfig.steps) {
                        const step = orgConfig.steps[stepKey];
                        for (const fieldKey in step.fields) {
                            const field = step.fields[fieldKey];
                            if (field.varName === key) {
                                return field.inputType;
                            }
                        }
                    }
                    return null;
                })();

                // Process dropdown values
                if (inputType === 'selectOneDropdown') {
                    // For single selects styled as multi
                    if (Array.isArray(value) && value.length > 0) {
                        value = value[0].value;
                    }
                    // For regular single selects
                    else if (value && typeof value === 'object' && 'value' in value) {
                        value = value.value;
                    }
                } else if (inputType === 'selectMultipleDropdown') {
                    // For multi-selects, extract the value from each item
                    if (Array.isArray(value)) {
                        value = value.map(item => item.value);
                    }
                }

                // Store in the right location
                if (isCustomField) {
                    customFields[key] = value;
                } else {
                    processedData[key] = value;
                }
            });

            // Add customFields to processedData
            processedData.customFields = customFields;

            // The server rejects a missing role. Check it here as well so the
            // admin sees the reason before we create an account or an invite.
            const roleCheck = normaliseProviderRole(
                typeof customFields.role === 'string' ? customFields.role : ''
            );
            if (!roleCheck.ok) {
                setSubmitError(roleCheck.message);
                setIsSubmitting(false);
                return;
            }
            customFields.role = roleCheck.role;

            // Map UI "user type" to backend `userType` + the new clinical assignability flag.
            //
            // Why this exists:
            // - The UI has four choices (Clinician, Support, Clinical Org. Admin, Non-Clinical Org. Admin)
            // - The backend and Firestore Rules only understand three `userType` values:
            //     Clinician | Support | Org. Admin
            // - We carry the clinical/non-clinical distinction separately via `isAssignableClinician`.
            const selectedUiUserType = processedData.userType;
            const mapped = mapUiUserTypeToBackend(selectedUiUserType);
            if (!mapped.userType || typeof mapped.isAssignableClinician !== 'boolean') {
                throw new Error('Invalid user type selection. Please choose a valid user type.');
            }

            const setupNewClinician = httpsCallable(functions, 'setupNewClinician');
            // Determine which auth mode to send to the backend.
            //
            // - For most orgs, this is centrally managed by orgConfig.authMode (default: hybrid).
            // - For SLaM/3 Borough in dev/staging, we allow a UI override for testing.
            //
            // IMPORTANT:
            // - Production should remain unaffected: the backend will ignore any override attempts
            //   and enforce SLaM/3 Borough as Microsoft-first invite onboarding.
            const effectiveAuthMode = shouldShowAuthModeSelector
                ? selectedAuthMode
                : (canSelectSSO ? (orgConfig?.authMode || 'hybrid') : 'hybrid');
            // Call the cloud function with processed data
            const result = await setupNewClinician({
                title: processedData.title,
                firstName: processedData.firstName?.trim(),
                lastName: processedData.lastName?.trim(),
                email: processedData.email?.toLowerCase(),
                // Backwards compatible userType for authorization decisions (do not change values).
                userType: mapped.userType,
                // New flag used to decide whether this account should be selectable/assignable as clinical staff.
                // Back-end support will be added as part of the wider rollout; extra keys are ignored safely today.
                isAssignableClinician: mapped.isAssignableClinician,
                customFields: processedData.customFields || {},
                organisation: userData.organisation,
                // Auth mode chosen per-organisation; if the project does not allow SSO, fall back to hybrid.
                authMode: effectiveAuthMode,
                // Pathway specialisms control which patient assessment pathways this
                // clinician can be assigned to. Defaults to ['adultAdhd'] for
                // backwards compatibility with single-pathway orgs.
                pathwaySpecialisms: pathwaySpecialisms,
            });

            if (result.data.success) {
                // New Microsoft-first onboarding path (SLaM / 3 Borough):
                // If the backend returns an inviteId, it means:
                // - No Firebase Auth user was created yet
                // - No clinicians/{uid} record exists yet
                // - The invited user must sign in with Microsoft within the invite validity window
                //
                // We surface a clear confirmation message so Org Admins understand why the new user
                // does not appear in the clinicians list immediately.
                const inviteId = result?.data?.inviteId;
                const expiresAtIso = result?.data?.expiresAtIso;
                if (inviteId) {
                    const expiresPretty = (() => {
                        try {
                            return expiresAtIso ? new Date(expiresAtIso).toLocaleString('en-GB') : '7 days';
                        } catch (_) {
                            return '7 days';
                        }
                    })();
                    setSubmitSuccess(
                        `Invite created successfully. The user must sign in with Microsoft within 7 days (expires: ${expiresPretty}). ` +
                        `Invite ID: ${inviteId}`
                    );

                    // We still refresh local data (even though clinicians list won't change yet).
                    // This keeps dashboards consistent with any other context refresh side-effects.
                    await refreshUserData();

                    // Reset form and auto-close after a short delay so the admin can see the message.
                    setFormData({
                        title: '',
                        firstName: '',
                        lastName: '',
                        email: '',
                        userType: '',
                        customFields: {}
                    });
                    setPathwaySpecialisms(['adultAdhd']);
                    setTimeout(() => {
                        try { onClose(); } catch (_) {}
                    }, 2500);
                } else {
                    // Legacy path: Auth user + clinicians doc created immediately.
                await refreshUserData();
                // Reset form to initial state
                setFormData({
                    title: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                    userType: '',
                    customFields: {}
                });
                setPathwaySpecialisms(['adultAdhd']);
                onClose();
                }
            } else if (result.data.code === "ALREADY_EXISTS") {
                setSubmitError('The email address provided is already in use. Please use a different email address.');
            } else {
                setSubmitError(result.data.message || 'Failed to create account');
            }
        } catch (error) {
            console.error('Error creating provider account:', error);
            
            // Improve error handling to show specific messages
            if (error.code === 'functions/already-exists') {
                setSubmitError('A user with this email already exists. Please use a different email address.');
            } else if (error.code === 'functions/invalid-argument') {
                // Extract validation error message
                setSubmitError(error.message || 'Invalid input. Please check your form data.');
            } else if (error.message) {
                // Extract message from Firebase error structure
                const errorMessage = error.message.includes('Firebase:') 
                    ? error.message.split('Firebase:')[1].trim() 
                    : error.message;
                setSubmitError(errorMessage || 'An unexpected error occurred. Please try again.');
            } else {
                setSubmitError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Render field based on its type
    const renderField = (field) => {
        if (!field || !field.varName || !field.inputType) {
            console.error("Invalid field configuration:", field);
            return null;
        }

        const { varName, displayText, inputType, placeholder, options, description, required, maxLength } = field;

        switch (inputType) {
            case 'freeText':
                // Determine if this field needs input sanitization
                // Name fields: only allow letters, spaces, hyphens, apostrophes
                // Email fields: only allow valid email characters
                const isNameField = varName === 'firstName' || varName === 'lastName';
                const isEmailField = varName === 'email';
                const roleCheck = varName === 'role'
                    ? normaliseProviderRole(typeof formData[varName] === 'string' ? formData[varName] : '')
                    : null;
                // Only nag once the admin has typed something that still is not
                // a usable job title (spaces, a line break, or too many characters).
                // An empty box is explained by the asterisk and the hint.
                const showRoleError = Boolean(
                    roleCheck && !roleCheck.ok && String(formData[varName] || '').trim()
                );

                return (
                    <div key={varName} className="mb-4">
                        <label htmlFor={varName} className="block text-sm font-medium text-gray-700 mb-1">
                            {displayText}{required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {description && (
                            <p className="text-sm text-gray-500 mb-2">{description}</p>
                        )}
                        <input
                            id={varName}
                            type="text"
                            value={formData[varName] || ''}
                            maxLength={typeof maxLength === 'number' ? maxLength : undefined}
                            aria-required={required ? 'true' : undefined}
                            aria-invalid={showRoleError ? 'true' : undefined}
                            onChange={(e) => {
                                let sanitizedValue = e.target.value;
                                // Apply appropriate sanitization based on field type
                                if (isNameField) {
                                    sanitizedValue = sanitizeNameInput(sanitizedValue);
                                } else if (isEmailField) {
                                    sanitizedValue = sanitizeEmailInput(sanitizedValue);
                                }
                                handleInputChange(varName, sanitizedValue);
                            }}
                            placeholder={placeholder}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                        />
                        {showRoleError && (
                            <p className="text-xs text-red-600 mt-1" role="alert">{roleCheck.message}</p>
                        )}
                    </div>
                );

            case 'freeTextNumerical':
                return (
                    <div key={varName} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {displayText}{required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                            type="number"
                            value={formData[varName] || ''}
                            onChange={(e) => handleInputChange(varName, e.target.value)}
                            placeholder={placeholder}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                        />
                    </div>
                );

            case 'selectOneDropdown':
                // Start from the raw options defined in the organisation config.
                // For some fields (e.g. userType) we further filter the options based on
                // the current user's role so that Support staff cannot elevate others to Org. Admin.
                let singleSelectOptions = options || [];

                if (varName === 'userType' && userData?.userType === 'Support') {
                    // Support users are front‑of‑house staff. They are allowed to invite
                    // new Clinician and Support users, but must not be able to create
                    // new Org. Admin accounts. We enforce that in the UI here and again
                    // in the backend Cloud Function for defence in depth.
                    //
                    // Note: Our UI exposes two variants of Org. Admin ("Clinical" and "Non‑Clinical").
                    // For permission purposes they are still both Org Admin. So Support staff must not
                    // see either option here.
                    singleSelectOptions = singleSelectOptions.filter(opt =>
                        opt?.value === UI_USER_TYPES.CLINICIAN || opt?.value === UI_USER_TYPES.SUPPORT
                    );
                }

                return (
                    <div key={varName} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {displayText}{required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {description && (
                            <p className="text-sm text-gray-500 mb-2">{description}</p>
                        )}
                        <Select
                            {...PROVIDER_SETUP_SELECT_MENU_PROPS}
                            isMulti
                            options={singleSelectOptions}
                            value={Array.isArray(formData[varName]) ? formData[varName] : (formData[varName] ? [formData[varName]] : [])}
                            onChange={(selected) => {
                                // Always keep only the most recently selected item
                                const newSelection = selected.length > 0 ? [selected[selected.length - 1]] : [];
                                handleInputChange(varName, newSelection, true);
                            }}
                            className="mt-1"
                            placeholder={placeholder}
                            closeMenuOnSelect={true}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.shiftKey) {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </div>
                );

            case 'selectMultipleDropdown':
                const multiSelectOptions = options || [];

                return (
                    <div key={varName} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {displayText}{required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {description && (
                            <p className="text-sm text-gray-500 mb-2">{description}</p>
                        )}
                        <Select
                            {...PROVIDER_SETUP_SELECT_MENU_PROPS}
                            isMulti
                            options={multiSelectOptions}
                            value={formData[varName] || []}
                            onChange={(selected) => handleInputChange(varName, selected)}
                            className="mt-1"
                            placeholder={placeholder}
                            closeMenuOnSelect={false}
                        />
                    </div>
                );

            default:
                return <div key={varName}>Unsupported field type: {inputType}</div>;
        }
    };

    // Render step content with proper null checks
    const renderStepContent = () => {
        // Make sure currentStep is a string for object access
        const stepKey = currentStep.toString();

        // Safely access the step configuration
        if (!orgConfig || !orgConfig.steps || !orgConfig.steps[stepKey]) {
            return <div>Step configuration not found</div>;
        }

        const stepConfig = orgConfig.steps[stepKey];

        return (
            <div>
                <h3 className="text-lg font-medium mb-4">{stepConfig.title || `Step ${currentStep}`}</h3>

                <div className="space-y-4">
                    {/* Safely iterate through fields with null checks */}
                    {stepConfig.fields && Object.keys(stepConfig.fields).map(fieldKey => {
                        const field = stepConfig.fields[fieldKey];
                        if (field) {
                            return renderField(field);
                        }
                        return null;
                    })}
                </div>
            </div>
        );
    };

    return (
        <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <AlertDialog.Portal>
                <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm" />
                <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto">

                    <div className="flex justify-between items-start">
                        <AlertDialog.Title className="text-xl font-semibold mb-4">
                            Set Up New User
                        </AlertDialog.Title>
                        <ButtonExitModal text="Cancel"/>
                    </div>

                    <AlertDialog.Description className="sr-only">
                        Complete the following fields to create a new user account. This pop-up contains required fields and a multi-step process.
                    </AlertDialog.Description>

                    <div className="mb-4">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-500">
                                Step {currentStep} of {effectiveTotalSteps}
                            </div>
                            <div className="flex h-2 rounded-full bg-gray-200 w-32">
                                <div
                                    className="h-full bg-violet-500 rounded-full transition-all duration-300 ease-in-out"
                                    style={{ width: `${(currentStep / effectiveTotalSteps) * 100}%` }}
                                />
                            </div>
                        </div>
                    </div>

                    {shouldShowAuthModeSelector && (
                        <div className="mb-4 p-3 rounded-md border border-blue-200 bg-blue-50">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-sm font-semibold text-blue-900">Sign-in mode (dev/staging only)</div>
                                    <div className="text-xs text-blue-800 mt-1">
                                        This override exists for testing. Production ignores this choice and enforces Microsoft-first onboarding for these organisations.
                                    </div>
                                </div>
                                <div className="min-w-[160px]">
                                    <label className="sr-only" htmlFor="td-authmode-override">Auth mode</label>
                                    <select
                                        id="td-authmode-override"
                                        value={selectedAuthMode}
                                        onChange={(e) => setSelectedAuthMode(String(e.target.value || '').toLowerCase())}
                                        className="w-full p-2 text-sm border border-blue-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                                    >
                                        {authModeOptionsForUi.map((mode) => (
                                            <option key={mode} value={mode}>
                                                {mode === 'with_microsoft_sso' ? 'Microsoft SSO (invite-based)' : mode === 'hybrid' ? 'Email & password' : mode}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="my-4">
                        {renderStepContent()}

                        {/* Pathway selector
                            Rendered on the final wizard step for assignable clinical user types
                            (Clinician, Clinical Org. Admin) when the organisation supports
                            multiple assessment pathways. At least one pathway must remain
                            selected — the onChange handler prevents unchecking the last box. */}
                        {isFinalStep && showPathwaySpecialisms && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Pathways<span className="text-red-500 ml-0.5">*</span>
                                </label>
                                <p className="text-sm text-gray-500 mb-2">
                                    Select which assessment pathways this user can be assigned to.
                                </p>
                                <div className="grid grid-cols-1 gap-2">
                                    {selectablePathwaySpecialisms.map((specialism) => {
                                        const isSelected = pathwaySpecialisms.includes(specialism);
                                        return (
                                            <button
                                                key={specialism}
                                                type="button"
                                                onClick={() => {
                                                    if (isSelected) {
                                                        const next = pathwaySpecialisms.filter(s => s !== specialism);
                                                        if (next.length > 0) setPathwaySpecialisms(next);
                                                    } else {
                                                        setPathwaySpecialisms(prev => [...prev, specialism]);
                                                    }
                                                }}
                                                className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                                    isSelected
                                                        ? 'bg-violet-100 text-violet-800 border border-violet-300 shadow-sm'
                                                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                                                }`}
                                            >
                                                <div className={`w-4 h-4 rounded border-2 mr-3 flex items-center justify-center ${
                                                    isSelected
                                                        ? 'bg-violet-600 border-violet-600'
                                                        : 'border-gray-300 bg-white'
                                                }`}>
                                                    {isSelected && (
                                                        <CheckIcon className="w-3 h-3 text-white" />
                                                    )}
                                                </div>
                                                <span>{PATHWAY_SPECIALISM_LABELS[specialism] || specialism}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {submitError && (
                        <div className="p-3 mt-3 text-sm font-semibold text-red-600 bg-red-50 rounded-md">
                            {submitError}
                        </div>
                    )}

                    {submitSuccess && (
                        <div className="p-3 mt-3 text-sm font-semibold text-green-700 bg-green-50 rounded-md">
                            {submitSuccess}
                        </div>
                    )}

                    <div className="mt-6 flex justify-between">
                        {currentStep > 1 ? (
                            <button
                                type="button"
                                onClick={handleBack}
                                className="px-4 py-2 text-sm font-semibold rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
                            >
                                <div className="flex items-center">
                                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                                    Back
                                </div>
                            </button>
                        ) : (
                            <div></div> // Empty div to maintain flex spacing
                        )}

<button
                            type="button"
                            onClick={handleNext}
                            disabled={!isStepValid() || isSubmitting}
                            className={`px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors
            ${!isStepValid() || isSubmitting
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-violet-600 hover:bg-violet-700'}`}
                        >
                            {isSubmitting ? (
                                <div className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processing...
                                </div>
                            ) : isFinalStep ? (
                                'Submit'
                            ) : (
                                <div className="flex items-center">
                                    Next
                                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                                </div>
                            )}
                        </button>
                    </div>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
}

export default ProviderAccountSetupForm;